import './icons/faIconsConfig.js';
import type { VideoLanguageKind } from './types';
import type { ResolvedVideoPlayerConfig } from './internal-types';

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
export default class VideoDOM {
  /** Set by {@link VideoDOM.setConfig} before any of the markup methods run. */
  declare config: ResolvedVideoPlayerConfig;

  constructor() {
    // Left unset until setConfig() runs, as in the original JS.
    this.config = null as unknown as ResolvedVideoPlayerConfig;
  }

  setConfig(config: ResolvedVideoPlayerConfig) {
    this.config = config;
  }

  makeListElements(source: 'audioTracks', type: VideoLanguageKind) {
    let html = '';

    const sortedLanguages = this.config[source].sort((a, b) => a.language.localeCompare(b.language));

    sortedLanguages.forEach(({ langCode, language }) => {
      if (langCode === this.config.activeAudioTrack && type === 'audio') {
        html += `<li class="list-item active" data-type="${type}" data-lang="${langCode}"><span class="fas fa-check"></span><button>${capitalize(
          language,
        )}</button></li>`;
      } else {
        html += `<li class="list-item" data-type="${type}" data-lang="${langCode}"><span class="fas fa-check"></span><button>${capitalize(
          language,
        )}</button></li>`;
      }
    });

    return html;
  }

  makeSubtitleListElements(source: 'subtitleTracks', type: VideoLanguageKind) {
    let html = '';

    const sortedLanguages = this.config[source].sort((a, b) => a.language.localeCompare(b.language));

    if (this.config.activeSubtitleTrack !== '') {
      html += `<li data-lang="" class="list-item"><span class="fas fa-check"></span><button>${this.config.offCaption}</button></li>`;
    } else {
      html += `<li data-lang="" class="list-item active"><span class="fas fa-check"></span><button>${this.config.offCaption}</button></li>`;
    }

    sortedLanguages.forEach(({ langCode, language }) => {
      if (langCode === this.config.activeSubtitleTrack) {
        html += `<li class="list-item active" data-type="${type}" data-lang="${langCode}"><span class="fas fa-check"></span><button>${capitalize(
          language,
        )}</button></li>`;
      } else {
        html += `<li class="list-item" data-type="${type}" data-lang="${langCode}"><span class="fas fa-check"></span><button>${capitalize(
          language,
        )}</button></li>`;
      }
    });

    return html;
  }

  getAudioControlsMobile() {
    return `
            <dialog class="js_video-controls-menu video-controls__context-menu video-controls__context-menu__audio" id="audio-language-wrapper-mobile_${
              this.config.videoID
            }" inert aria-hidden="true">
            <div class="video-controls__context-menu__audio--top">
                <span class="fas fa-globe"></span>
                <span>${this.config.audioCaption} & ${this.config.subtitleCaption}</span>
                <button class="close" aria-label="${
                  this.config.i18n ? this.config.i18n('button_close') : 'Stäng'
                }"><iconify-icon width="24" icon="mdi:close"></iconify-icon></button>
            </div>
            <div class="video-controls__context-menu__audio--lists-wrapper">
              <div>
                  <div class="audio_controls-heading">
                      <span>${this.config.audioCaption}</span>
                  </div>
                  <ul id="audio-language-mobile_${this.config.videoID}" aria-label="${
                    this.config.i18n ? this.config.i18n('film.label_list_audio_tracks') : 'Ljudspår'
                  }">
                      ${this.makeListElements('audioTracks', 'audio')}
                  </ul>
              </div>
              <div>
                  <div class="audio_controls-heading">
                      <span>${this.config.subtitleCaption}</span>
                  </div>

                  <ul id="subtitle-language-mobile_${this.config.videoID}" aria-label="${
                    this.config.i18n ? this.config.i18n('film.label_list_subtitle_tracks') : 'Undertexter'
                  }">
                      ${this.makeSubtitleListElements('subtitleTracks', 'subtitle')}
                  </ul>
              </div>
            </div>
            </dialog>
        `;
  }

  getAudioControls() {
    return `<div class="video-controls__element">
              <button aria-label="${
                this.config.i18n ? this.config.i18n('film.audioAndSubtitles') : 'Ljud och undertexter'
              }" class="video-controls__button video-controls__button--audio js_video-preserve-delete-is-active" id="audio-language-button_${
                this.config.videoID
              }">
                  <span class="fas fa-globe js_video-preserve-delete-is-active"></span>
              </button>
            </div>
        `;
  }

  makePlaybackRateMenu() {
    return `
    <div class="slider-wrapper">
      <button id="playbackrate-decrement_${this.config.videoID}" class="btn">–</button>

      <input
        type="range"
        id="playback_slider_${this.config.videoID}"
        min="0"
        max="${this.config.playbackRates.length - 1}"
        step="1"
        value="2"
        aria-label="${this.config.i18n ? this.config.i18n('film.label_playback_speed_slider') : 'Ändra hastighet'}"
      />

      <button id="playbackrate-increment_${this.config.videoID}" class="btn">+</button>
    </div>
    `;
  }

  getPlaybackRateControl() {
    return `
        <div class="video-controls__element">
            <button aria-label="${
              this.config.i18n ? this.config.i18n('film.playbackSpeed') : 'Hastighet'
            }" id="playback_${
              this.config.videoID
            }" class="video-controls__button video-controls__button--playback-rate js_video-preserve-delete-is-active">
                  <iconify-icon width="24" class="js_video-preserve-delete-is-active" icon="mdi:speedometer"></iconify-icon>
            </button>
        </div>
         <div id="playback_list_wrap_${
           this.config.videoID
         }" class="js_video-controls-menu video-controls__context-menu video-controls__context-menu__playback" inert aria-hidden="true">
                <div style="display: flex; align-items: center; gap: 4px">
                  <iconify-icon icon="mdi:speedometer"></iconify-icon>
                  <span>${
                    this.config.i18n ? this.config.i18n('film.playbackSpeed') : 'Hastighet'
                  }</span><span id="playbackrate-value_${this.config.videoID}"></span>
                  <button class="close" aria-label="${
                    this.config.i18n ? this.config.i18n('button_close') : 'Stäng'
                  }"><iconify-icon width="24" icon="mdi:close"></iconify-icon></button>
                </div>

                <div id="playback_list_${this.config.videoID}">
                    ${this.makePlaybackRateMenu()}
                </div>
            </div>
        `;
  }

  getSubtitleSizeControls() {
    if (!this.config.enabledButtons.includes('subtitleSize')) {
      return '';
    }
    return `
        <div class="video-controls__element">
            <button aria-label="${
              this.config.i18n ? this.config.i18n('film.subtitleSize') : 'Textstorlek'
            }" class="video-controls__button js_video-preserve-delete-is-active" id="subtitle-size_${
              this.config.videoID
            }">
            <span class="fas fa-text-size js_video-preserve-delete-is-active"></span>
            </button>
        </div>
        <div class="js_video-controls-menu video-controls__context-menu video-controls__context-menu__subtitle-size" id="subtitle-size-menu_${
          this.config.videoID
        }" inert aria-hidden="true">
          <div style="display: flex; align-items: center; gap: 4px">
            <span class="fas fa-text-size"></span>
            <span>${this.config.i18n ? this.config.i18n('film.subtitleSize') : 'Textstorlek'}</span>
            <button class="close" aria-label="${
              this.config.i18n ? this.config.i18n('button_close') : 'Stäng'
            }"><iconify-icon width="24" icon="mdi:close"></iconify-icon></button>
          </div>

          <div class="slider-wrapper">
            <button id="subtitle-size-decrement_${this.config.videoID}" class="btn">–</button>
              <input
                type="range"
                id="subtitle-size-slider_${this.config.videoID}"
                min="0"
                max="3"
                step="1"
                value="1"
                aria-label="${this.config.i18n ? this.config.i18n('film.label_subtitle_size_slider') : 'Ändra textstorlek'}"
              />
            <button id="subtitle-size-increment_${this.config.videoID}" class="btn">+</button>
          </div>
        </div>
        `;
  }

  getTheatreControls() {
    if (!this.config.enabledButtons.includes('theatre')) {
      return '';
    }
    return `<div class="video-controls__element show-for-medium">
            <button class="video-controls__button video-controls__button--theater" id="theater-mode_${this.config.videoID}">
                <span class="icon-theatermode"></span>
            </button>
        </div>`;
  }

  getFullscreenControls() {
    if (!this.config.enabledButtons.includes('fullscreen')) {
      return '';
    }
    return `<div class="video-controls__element">
              <button aria-label="${
                this.config.i18n ? this.config.i18n('film.fullscreen') : 'Fullskärm'
              }" class="video-controls__button video-controls__button--full-screen" id="fullscreen_${
                this.config.videoID
              }">
                <span class="fas fa-chevron-left"></span>
              </button>
            </div>`;
  }

  getFullscreenControlsDesktop() {
    if (!this.config.enabledButtons.includes('fullscreen') || this.config.isMobileOrIpad) {
      return '';
    }
    return `<div class="video-controls__element">
              <button aria-label="${
                this.config.i18n ? this.config.i18n('film.fullscreen') : 'Fullscreen'
              }" class="video-controls__button video-controls__button--full-screen--desktop" id="fullscreen_desktop_${
                this.config.videoID
              }">
                <span class="fas fa-expand"></span>
              </button>
            </div>`;
  }

  makeScrubber() {
    return `<div class="scrubber-area" id="scrubber-area_${this.config.videoID}">
            <div class="scrubber is-active" id="scrubber_${this.config.videoID}">
                <div class="video-controls__element--timestamp">
                    <span class="timestamp" id="timestampCurrent_${this.config.videoID}">00:00</span>
                    <span class="timestamp" id="timestampDuration_${this.config.videoID}">00:00</span>
                </div>
                <div class="previewDuration" id="previewDuration_${this.config.videoID}"></div>
                <div class="timestamp-bullet" id="timestamp-hover_${this.config.videoID}">00:04</div>
                <div class="scrubber-background" id="scrubber-background_${this.config.videoID}"></div>
                <div
                  class="bar"
                  tabindex="0"
                  role="slider"
                  aria-label="${this.config.i18n ? this.config.i18n('film.label_seek_slider') : 'Sökreglage'}"
                  aria-valuemin="0"
                  aria-valuenow="53"
                  aria-valuetext="${
                    this.config.i18n
                      ? `0 ${this.config.i18n('film.scrubber_minutes')} 0 ${this.config.i18n('film.scrubber_seconds')}`
                      : '0 minuter 0 sekunder'
                  }"
                  id="scrubber-progress_${this.config.videoID}">
                    <div id="scrubber-button_${this.config.videoID}" class="video-controls__button scrubber-button">
                      <div></div>
                    </div>
                </div>
            </div>
        </div>`;
  }

  getVolumeSlider() {
    return `<div class="volume-slider-container show-for-medium" id="volume-slider-container">
           <input type="range" min="0" max="100" value="50" class="volume-slider" style="padding: 0 0 0 0; height: 5px;" id="volumeslider_${this.config.videoID}" aria-label="${
             this.config.i18n ? this.config.i18n('film.label_volume_slider') : 'Volymreglage'
           }"/>
        </div>`;
  }

  getVolumeControls() {
    return `
        <div class="video-controls__element video-controls__element--volume ${
          this.config.iOsDevice.iphone || this.config.iOsDevice.ipad ? 'hide-ios-touch' : ''
        }">
            <button aria-label="${
              this.config.i18n ? this.config.i18n('film.soundOff') : 'Ljud av'
            }" class="video-controls__button video-controls__button--volume" id="volume_${this.config.videoID}">
                <span class="fas fa-volume"></span>
            </button>
            ${this.getVolumeSlider()}
        </div>
        `;
  }

  getVideoTitle() {
    if (!this.config.trackName) {
      return '';
    }
    return `
            <span class="video-title">${this.config.trackName}</span>
       `;
  }

  getPlayButtonMobile() {
    return `<button aria-label="${
      this.config.i18n ? this.config.i18n('film.play') : 'Spela'
    } ${this.config.trackName}" class="video-controls__button video-controls__button--play-mobile"
            id="play_mobile_${this.config.videoID}">
      <i class="fas fa-play fa-lg"></i>
    </button>`;
  }

  getPlayButtonDesktop() {
    return `<button aria-label="${
      this.config.i18n ? this.config.i18n('film.play') : 'Spela'
    } ${this.config.trackName}" class="video-controls__button video-controls__button--play-desktop"
            id="play_desktop_${this.config.videoID}">
      <i class="fas fa-play"></i>
    </button>`;
  }

  getDoubleTapOverlays() {
    return `
      <div class="double-tap-overlay double-tap-overlay--rewind" id="double-tap-rewind_${this.config.videoID}" aria-hidden="true">
        <span class="fas fa-backward"></span>
      </div>
      <div class="double-tap-overlay double-tap-overlay--forward" id="double-tap-forward_${this.config.videoID}" aria-hidden="true">
        <span class="fas fa-forward"></span>
      </div>
    `;
  }

  getRewindButton() {
    return `<button aria-label="${
      this.config.i18n ? this.config.i18n('film.rewind') : 'Spola tillbaka 10 sekunder'
    }" class="video-controls__button video-controls__button--rewind"
            id="double-tap-rewind-btn_${this.config.videoID}">
      <span class="fas fa-arrow-rotate-left-10 fa-lg"></span>
    </button>`;
  }

  getForwardButton() {
    return `<button aria-label="${
      this.config.i18n ? this.config.i18n('film.forward') : 'Hoppa fram 10 sekunder'
    }" class="video-controls__button video-controls__button--forward"
            id="double-tap-forward-btn_${this.config.videoID}">
      <span class="fas fa-arrow-rotate-right-10 fa-lg"></span>
    </button>`;
  }

  writeDOM() {
    let version = 'unknown';
    if (typeof __VERSION__ !== 'undefined') {
      version = __VERSION__;
    }
    return `
        <div class="${this.config.isMobileOrIpad ? 'isMobileOrIpad' : ''} fullscreen-wrap notranslate" id="${
          this.config.fullscreenWrapID
        }">
            <div class="video-wrap" id="${this.config.videoWrapID}" data-player-version="${version}">
                <video id="${this.config.videoID}" playsinline class="video-js"></video>
                ${this.getDoubleTapOverlays()}
                ${
                  this.config.showSkipIntroBtn
                    ? `<button id="video-controls__skip-intro_${this.config.videoID}" class="video-controls__skip-intro">${this.config.skipIntroCaption}</button>`
                    : ''
                }
                <div class="video-controls is-active" id="video-controls_${this.config.videoID}">
                        ${this.getAudioControlsMobile()}
                    <div class="controls-section top">
                      <div style="display: flex; align-items: center">
                          ${this.getFullscreenControls()}
                          ${this.getVideoTitle()}
                      </div>
                    </div>
                    <div class="controls-section center">
                      <div class="video-controls__element video-controls__element--center-controls">
                          ${this.getRewindButton()}
                          ${this.getPlayButtonMobile()}
                          ${this.getForwardButton()}
                      </div>
                    </div>
                    <div class="controls-section bottom">
                        ${this.makeScrubber()}
                        ${this.getTheatreControls()}
                        <div class="bottom--buttons">
                          <div>
                          <!--<button class="video-controls__button">
                            <span class="fas fa-backward-step"></span>
                          </button>-->
                          <div style="display: inline-flex">
                              ${this.getPlayButtonDesktop()}
                          </div>
                              ${this.getVolumeControls()}
                          </div>
                          <div style="display: flex; gap: 8px">
                              ${this.getPlaybackRateControl()}
                              ${this.getAudioControls()}
                              ${this.getSubtitleSizeControls()}
                              ${this.getFullscreenControlsDesktop()}
                          </div>
                    </div>
                    </div>
                </div>
            </div>
        </div>`;
  }
}
